/// <amd-module name="typeahead/core/presentation/SuggestionRendererUtils" />
/// <amd-dependency path="typeahead/core/loc" name="loc"/>

import {locationIcon} from "typeahead/core/geolocation/GeolocationIcon";
import {IEssSuggestionResult, ISuggestionResult} from "typeahead/core/Suggestion";
import {Lob, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

export class SuggestionRendererUtils {
    /*
        al refers to alias, e.g for New York it is "Big Apple".
        ap refers to the airport name string, e.g. "(NYC - All Airports)"
        the point of the tags is:
        1. tech debt: have a better way to parse data from the server rather than using paren position. in the future we
        would ideally have all parsing based on tags rather than position.
        2. support flights variant 2: so we can distinguish ap and al from each other (is in a different order depending
        on serverside bucketing and may have "extra text" in between)
        Also Refer -
        * https://eiwork.mingle.thoughtworks.com/projects/ewe_discovery_services/cards/3534
        * https://eiwork.mingle.thoughtworks.com/projects/ewe_discovery_services/cards/6135
        * https://jira.expedia.biz/browse/ECOMSVC-2412
        * https://jira.expedia.biz/browse/ECOMSVC-2936
    */
    public static alOpenTag: string = "<al>";
    public static alCloseTag: string = "</al>";
    public static apOpenTag: string = "<ap>";
    public static apCloseTag: string = "</ap>";

    /*
        All requests with consistent_display feature flag should have the following 3 tags:
        <fl> (first line)               is usually short name
        <fg> (optional) (first grey)    could be alias / tla / hotel filter
        <sl> (second line)              is the expanded parent information

        UI should display as:
        <fl>New York</fl> <fg>Big Apple</fg>
        <sl>New York, United States</sl>

        <cdt> tags are used for the labels, we append this to the returned suggestions if were in variant 2 and parse it
        if they are present
     */
    public static flOpenTag: string = "<fl>";
    public static flCloseTag: string = "</fl>";
    public static fgOpenTag: string = "<fg>";
    public static fgCloseTag: string = "</fg>";
    public static slOpenTag: string = "<sl>";
    public static slCloseTag: string = "</sl>";
    public static nearOpenTag: string = "<near>";
    public static nearCloseTag: string = "</near>";

    public static cdtOpenTag: string = "<cdt>";
    public static cdtCloseTag: string = "</cdt>";

    public static getHtml(suggestionResult: ISuggestionResult): string {
        function appendDistanceForNearbyAirportIfApplicable(html) {
            // This feature is currently limited to en_GB and en_US because it is not yet localized. Proper localization
            // should translate "2 miles away" to "2 millas de distancia" for es_US and "3 km entfernt" for de_DE.
            const essSuggestionResult: IEssSuggestionResult = suggestionResult as IEssSuggestionResult;
            const localizedNearbyDistance: string = essSuggestionResult.hierarchyInfo &&
            essSuggestionResult.hierarchyInfo.nearby ? essSuggestionResult.hierarchyInfo.nearby.locDist : undefined;
            if (localizedNearbyDistance) {
                html += " " + localizedNearbyDistance;
            }
            return html;
        }

        function makeIconHtml() {
            if (suggestionResult.showAsHierarchy) {
                if (SuggestionRendererUtils.isConsistentDisplay(suggestionResult)) {
                    // @ts-ignore
                    if (suggestionResult.hierarchyInfo
                        // @ts-ignore
                        && suggestionResult.hierarchyInfo.relation
                        // @ts-ignore
                        && suggestionResult.hierarchyInfo.relation.indexOf("nearby") > -1) {
                        suggestionResult.icon = "flights";
                    } else {
                        suggestionResult.icon = "return";
                    }
                } else {
                    suggestionResult.icon = "return";
                }
            }

            let html;
            if (suggestionResult.icon === "geolocation") {
                html = `<span data-tid="typeahead-icon` + suggestionResult.index +
                    `" class="icon" aria-hidden="true">` +
                    `<img alt="" style="height:1em;width:1em;vertical-align:middle;" ` +
                    `src='` + locationIcon + `'/>` +
                    `</span>`;
            } else if (suggestionResult.icon === "none") {
                html = `<span data-tid="typeahead-icon`
                    + suggestionResult.index
                    + `" aria-hidden="true"></span>`;
            } else {
                html = `<span style = "` + (TypeaheadUtils.isRtl() ? "float:right" : "float:left")
                    + `" data-tid="typeahead-icon`
                    + suggestionResult.index
                    + `" class="icon" aria-hidden="true">
                    <svg class="uitk-icon-svg"` +
                    (suggestionResult.icon === "trainalt" ?
                        `style="height:18px;width:18px;margin-left:-4px;margin-top:-10px;top:4px;left:2px"` : ``) +
                    (suggestionResult.icon === "camera" ?
                        `style="height:16px;width:16px;margin-left:-2px;margin-top:-8px;top:4px;left:2px"` : ``) + `>
                        <use xlink:href="#uitk-icon-` + suggestionResult.icon + `"/>
                    </svg>
                    </span>`;
            }
            return html;
        }

        function convertMainToMultiLineHtml(mainHtml) {
            let primaryText: string = "";
            let bottomText: string = "";
            let extraText: string = "";
            const openParenIndex: number = mainHtml.indexOf("(");
            const closeParenIndex: number = mainHtml.indexOf(")");
            let tla;
            let rest;

            /* main suggestions should always have parentheses */
            if (openParenIndex !== -1 && closeParenIndex !== -1) {
                primaryText = mainHtml.substring(0, openParenIndex);
                bottomText = mainHtml.substring(openParenIndex + 1, closeParenIndex);
                const dashIndex: number = bottomText.indexOf("-");
                if (dashIndex !== -1) {
                    tla = bottomText.substring(0, dashIndex).replace(/&nbsp;/i, "").trim();
                    rest = bottomText.substring(dashIndex + 1, bottomText.length);
                } else {
                    tla = bottomText;
                    rest = "";
                }
                bottomText = "(<B>" + tla + "</B>)" + rest;
                if (closeParenIndex + 1 < mainHtml.length) {
                    extraText = mainHtml.substring(closeParenIndex + 1, mainHtml.length);
                }
            } else {
                primaryText = mainHtml;
            }
            return SuggestionRendererUtils.createMultiLineHtml(primaryText, extraText, bottomText);
        }

        function convertChildToMultiLineHtml(childHtml) {
            const dashIndex = childHtml.indexOf("-");
            if (dashIndex !== -1) {
                const tla = childHtml.substring(0, dashIndex).replace(/&nbsp;/i, "").trim();
                const rest = childHtml.substring(dashIndex + 1, childHtml.length);

                return "(<B>" + tla + "</B>)" + rest;
            } else {
                const result = new RegExp("^<ap>([A-Z]{3})<\\/ap>(.*)").exec(childHtml);
                if (result && result.length && result[1]) {
                    const tla = result[1];
                    let rest = result[2];
                    let displayName = "(<B>" + tla + "</B>)";
                    if (rest) {
                        rest = rest.trim();
                        if (rest.indexOf(",") === 0) {
                            rest = rest.substr(1, rest.length);
                        }
                        displayName += " " + rest;
                    }
                    return displayName;
                }
            }
            return childHtml;
        }

        function isFlightSearchResultsPage(): boolean {
            return "Flights.Search" === TypeaheadUtils.getClientId();
        }

        function doMultiline(): boolean {
            return (Lob.Flight === TypeaheadUtils.getLob() || isFlightSearchResultsPage());
        }

        function showHotelAddress(): boolean {
            return ("LX.Checkout" === TypeaheadUtils.getClientId() && suggestionResult.type !== "HOTELNOTPRESENT") ;
        }

        function formatText(html) {
            let result: string = html;
            if (doMultiline()) {
                if (suggestionResult.showAsHierarchy) {
                    result = convertChildToMultiLineHtml(html);
                } else {
                    // new logic occurs only when #24512 (tag scream test) & #24513 (flights display alias)
                    // are turned on, should be separate from existing logic in case of rollback / turn off
                    if (SuggestionRendererUtils.isApAlTagged(html)) {
                        result = SuggestionRendererUtils.convertApAlTaggedMainToMultiLineHtml(html);
                    } else {
                        result = convertMainToMultiLineHtml(html);
                    }
                }
            }
            return result;
        }

        // TODO: reusing doMultiline(), convertMainToMultiLineHtml()
        // and refactoring formatTextForLXCheckout() is pending

        function formatTextForLXCheckout() {
            let hotelAddressHtml: string;
            if ((suggestionResult as IEssSuggestionResult) && (suggestionResult as IEssSuggestionResult).hotelAddress) {
                hotelAddressHtml = (suggestionResult as IEssSuggestionResult).hotelAddress.street + ", " +
                    (suggestionResult as IEssSuggestionResult).hotelAddress.city + ", "
                    + (suggestionResult as IEssSuggestionResult).hotelAddress.province;
            }

            const hotelAddressSpanStyle: string = "display:block;" +
                "height:19px;" +
                "font-size:12px;" +
                "line-height:1.5;" +
                "color:#757575;";

            return `<span style="position:relative;bottom:0.6em">` + iconHtml + "</span>" +
                `<div style="display:inline-block;">
            <span class="text">` + formatText(textHtml) + "</span>" +
                `<span class="text" style=` + hotelAddressSpanStyle + ">" + hotelAddressHtml + "</span></div>" +
                (suggestionResult.type === "LIST" ? SuggestionRendererUtils.toggleIconHtml : "");
        }

        function makeIconAndTextHtml(hotelAddressDisplayed) {
            let html;
            if (hotelAddressDisplayed) {
                html = formatTextForLXCheckout();
            } else {
                html = iconHtml + `<span class="text">` + formatText(textHtml) + "</span>" +
                    (suggestionResult.type === "LIST" ? SuggestionRendererUtils.toggleIconHtml : "");
            }
            return html;
        }

        function getConsistentSecondLine() {
            const slOpenIdx: number = textHtml.indexOf("<sl");
            const slCloseIdx: number = textHtml.indexOf(SuggestionRendererUtils.slCloseTag)
                + SuggestionRendererUtils.slCloseTag.length;

            return textHtml.substring(slOpenIdx, slCloseIdx);
        }

        function formatConsistentText(html) {
            const flOpenIdx: number = html.indexOf(SuggestionRendererUtils.flOpenTag);
            const flCloseIdx: number = html.indexOf(SuggestionRendererUtils.flCloseTag)
                + SuggestionRendererUtils.flCloseTag.length;
            const fgOpenIdx: number = html.indexOf(SuggestionRendererUtils.fgOpenTag);
            const fgCloseIdx: number = html.indexOf(SuggestionRendererUtils.fgCloseTag)
                + SuggestionRendererUtils.fgCloseTag.length;

            const cdtOpenIdx: number = html.indexOf(SuggestionRendererUtils.cdtOpenTag);
            const cdtCloseIdx: number = html.indexOf(SuggestionRendererUtils.cdtCloseTag)
                + SuggestionRendererUtils.cdtCloseTag.length;

            let label = "";
            if (cdtOpenIdx < cdtCloseIdx - SuggestionRendererUtils.cdtCloseTag.length) {
                label = html.substring(cdtOpenIdx, cdtCloseIdx);
            }

            return SuggestionRendererUtils.
            createMultiLineHtmlWrapped(`<strong>${html.substring(flOpenIdx, flCloseIdx)}</strong>`,
                (fgOpenIdx < fgCloseIdx - SuggestionRendererUtils.fgCloseTag.length)
                    ? html.substring(fgOpenIdx, fgCloseIdx) : "",
                consistentSecondLine, true, label.length > 0) + label;
        }

        function makeConsistentIconHtml() {
            let html;
            if (suggestionResult.icon === "geolocation") {
                html = `<span data-tid="typeahead-icon${suggestionResult.index} class="icon" aria-hidden="true">
                            <img alt="" style="height:1em;width:1em;vertical-align:middle;" src='${locationIcon}'/>
                        </span>`;
            } else {
                html =
                    `<div style='display:inline-block;${consistentSecondLine.length >
                    SuggestionRendererUtils.slOpenTag.length + SuggestionRendererUtils.slCloseTag.length
                        ? "top:2px;" : ""}
                vertical-align:top' data-tid="typeahead-icon ${suggestionResult.index}" class="icon" aria-hidden="true">
                        <svg class="uitk-icon-svg" style=
                            ${suggestionResult.type === "METROSTATION" ?
                            "height:16px;width:16px;margin-left:-3px;top:1px;left:2px" : ""} >
                            <use xlink:href="#uitk-icon-${suggestionResult.icon}"/>
                        </svg>
                    </div>`;
            }

            if (suggestionResult.type === "METROSTATION") {
                html = html.replace("train", "trainalt");
            } else if (suggestionResult.type === "CITY" || suggestionResult.type === "MULTICITY"
                || suggestionResult.type === "METROCODE") {
                html = html.replace("flights", "location");
            }

            if (SuggestionRendererUtils.isNearbyAllAirports(suggestionResult)) {
                html = html.replace(suggestionResult.icon, "location");
            } else if (SuggestionRendererUtils.hasTla(suggestionResult)) {
                html = html.replace("location", "flights");
            }

            return html;
        }

        function makeConsistentIconAndTextHtml() {
            const html = consistentIconHtml + formatConsistentText(textHtml) +
                (suggestionResult.type === "LIST" ? SuggestionRendererUtils.toggleIconHtml : "");
            return html;
        }

        let textHtml = suggestionResult.regionNames.displayName;

        const iconHtml = makeIconHtml();

        const consistentSecondLine = getConsistentSecondLine();
        const consistentIconHtml = makeConsistentIconHtml();

        if (suggestionResult.showAsHierarchy) {
            textHtml = appendDistanceForNearbyAirportIfApplicable(textHtml);
        }

        let iconAndTextHtml;
        if (SuggestionRendererUtils.isConsistentDisplay(suggestionResult)) {
            iconAndTextHtml = makeConsistentIconAndTextHtml();
        } else {
            iconAndTextHtml = makeIconAndTextHtml(showHotelAddress());
        }

        return suggestionResult.showAsHierarchy ? (`<div class="child-suggestion" style="padding-left:` +
            ("none" === suggestionResult.icon ? `12px` : `1.88235em`) +
            `; width: 100%; text-overflow:ellipsis; overflow:hidden">` + iconAndTextHtml + `</div>`) : iconAndTextHtml;
    }

    public static  isConsistentDisplay(suggestion: ISuggestionResult) {
        if (suggestion && suggestion.regionNames && suggestion.regionNames.displayName) {
            return [SuggestionRendererUtils.flOpenTag, SuggestionRendererUtils.flCloseTag].every((tag) => {
                return suggestion.regionNames.displayName.indexOf(tag) !== -1;
            });
        }
        return false;
    }

    // expected input is "<ap>...</ap>". output depends on rearrange but should still be tagged
    public static formatApText(apText: string, rearrange: boolean): string {
        if (apText.indexOf(SuggestionRendererUtils.apOpenTag) !== -1) {
            apText = apText.replace(SuggestionRendererUtils.apOpenTag
                + "(", "").replace(")" + SuggestionRendererUtils.apCloseTag, "");

            const dashIndex: number = apText.indexOf("-");
            let tla = "";
            let rest = "";

            if (dashIndex !== -1) {
                tla = apText.substring(0, dashIndex).trim();
                rest = apText.substring(dashIndex + 1, apText.length);
            } else {
                tla = apText;
                rest = "";
            }

            if (rearrange) {    // output: "(<B>NYC</B>) - All Airports"
                apText = "(<B>" + tla + "</B>)" + rest;
            } else {            // output: "(<B>NYC</B> - All Airports)"
                apText = "(" + apText.replace(tla, "<B>" + tla + "</B>") + ")";
            }

            apText = SuggestionRendererUtils.apOpenTag + apText + SuggestionRendererUtils.apCloseTag;
        }
        return apText;
    }

    public static convertApAlTaggedMainToMultiLineHtml(textHtml) {
        const apOpenStartIndex: number = textHtml.indexOf(SuggestionRendererUtils.apOpenTag);
        const apCloseStartIndex: number = textHtml.indexOf(SuggestionRendererUtils.apCloseTag);
        const alOpenStartIndex: number = textHtml.indexOf(SuggestionRendererUtils.alOpenTag);
        const alCloseStartIndex: number = textHtml.indexOf(SuggestionRendererUtils.alCloseTag);

        const isApTagFirst: boolean = apOpenStartIndex < alOpenStartIndex;
        const leadingText: string = textHtml.substring(0, isApTagFirst ? apOpenStartIndex : alOpenStartIndex);
        let extraText = ""; // if present, seems to only be the near string
        // textHtml element ordering changes depending on serverside bucket
        if (isApTagFirst) {
            extraText = textHtml.substring(
                apCloseStartIndex + SuggestionRendererUtils.apCloseTag.length, alOpenStartIndex);
        } else {
            extraText = textHtml.substring(apCloseStartIndex +
                SuggestionRendererUtils.apCloseTag.length, textHtml.length);
        }

        const apText: string = textHtml.substring(
            apOpenStartIndex, apCloseStartIndex + SuggestionRendererUtils.apCloseTag.length);
        const alText: string = textHtml.substring(
            alOpenStartIndex, alCloseStartIndex + SuggestionRendererUtils.alCloseTag.length);

        let result: string = textHtml;
        if (isApTagFirst) {
            result = this.createMultiLineHtmlWrapped(leadingText + extraText,
                this.formatApText(apText, false), this.addAlVariantTwoStyleToTags(alText), false, false);
        } else {
            result = this.createMultiLineHtmlWrapped(leadingText + alText, extraText,
                this.formatApText(apText, true), false, false);
        }
        return result;
    }

    // it looks like currently there's no custom css for uta. this is a temporary workaround
    public static addAlVariantTwoStyleToTags(textHtml) {
        if (this.isAlTagged(textHtml)) {
            const styledAl: string = `<al style="font-weight:bold;font-size:12px;color:black">`;
            textHtml = textHtml.replace("(", "").replace(")", "");
            textHtml = textHtml.replace(SuggestionRendererUtils.alOpenTag, styledAl);
        }
        return textHtml;
    }

    public static isAlTagged(html) {
        return html.indexOf(SuggestionRendererUtils.alOpenTag) !== -1
            && html.indexOf(SuggestionRendererUtils.alCloseTag) !== -1;
    }

    public static isApTagged(html) {
        return html.indexOf(SuggestionRendererUtils.apOpenTag) !== -1
            && html.indexOf(SuggestionRendererUtils.apCloseTag) !== -1;
    }

    public static isApAlTagged(html) {
        return this.isApTagged(html) && this.isAlTagged(html);
    }

    // identical to createMultiLineHtml except it fixes the text wrap issue
    public static createMultiLineHtmlWrapped(topLeft, topRight, bottom, isConsistentDisplay, hasLabel) {
        let html: string = `<div class='multiLineDisplay details' style='`;
        if (isConsistentDisplay) {
            html += `display:inline-block;position:relative;top:1px;`;
        }

        html += `width:${hasLabel ? "66%" : "80%"}'>`;

        html += topLeft.trim();
        if (topRight) {
            html += " " + topRight.trim();
        }
        if (bottom) {
            html += "<br>" + bottom.trim();
        }
        html += "</div>";
        return html;
    }

    public static createMultiLineHtml(topLeft, topRight, bottom) {
        let html: string = "<div class='multiLineDisplay' style='text-overflow:ellipsis; overflow:hidden'>";
        html += topLeft.trim();
        if (topRight) {
            html += " " + topRight.trim();
        }
        if (bottom) {
            html += "<br>" + bottom.trim();
        }
        html += "</div>";
        return html;
    }

    public static getTextType(suggestion: ISuggestionResult): string {
        let suggestionType: string;

        switch (suggestion.type) {
            case "AIRPORT":
                suggestionType = loc.airportMessage || "Airport";
                break;
            case "METROCODE":
            case "CITY":
            case "MULTICITY":
                suggestionType = loc.city || "City";
                break;
            case "NEIGHBORHOOD":
                suggestionType = loc.neighborhoodMessage || "Neighborhood";
                break;
            case "POI":
                suggestionType = loc.landmarkMessage || "Landmark";
                break;
            case "ADDRESS":
                suggestionType = loc.taCategoryNameAddress || "Address";
                break;
            case "HOTEL":
                suggestionType = loc.hotelMessage || "Hotel";
                break;
            case "MULTIREGION":
                suggestionType = loc.region || "Region";
                break;
            case "TRAINSTATION":
                suggestionType = loc.trainStationMessage || "Train Station";
                break;
            case "METROSTATION":
                suggestionType = loc.metroStationMessage || "Metro Station";
                break;
            default:
                suggestionType = "";
                break;
        }

        if (SuggestionRendererUtils.isNearbyAllAirports(suggestion)) {
            suggestionType = loc.city || "City";
        } else if (SuggestionRendererUtils.hasTla(suggestion)) {
            suggestionType = loc.airportMessage || "Airport";
        }

        return suggestionType;
    }

    public static hasNearBy(suggestion: ISuggestionResult) {
        if (suggestion && suggestion.regionNames && suggestion.regionNames.displayName) {
            const displayName = suggestion.regionNames.displayName;
            return displayName.indexOf(this.nearOpenTag) !== -1 && displayName.indexOf(this.nearCloseTag) !== -1;
        }
        return false;
    }

    public static isNearbyAllAirports(suggestion: ISuggestionResult) {
        return !this.hasTla(suggestion) && this.hasNearBy(suggestion);
    }

    public static formatConsistentDisplayType(suggestionType: string) {
        return `${this.cdtOpenTag}<div style='color:rgb(97, 97, 97);font-size:12px;margin-top:.5em;`
            + SuggestionRendererUtils.getDirectionalCss()
            + `'>${suggestionType}</div>${this.cdtCloseTag}`;
    }

    public static formatTypeDesktop(suggestionType: string) {
        return `<span style = 'color:rgb(97, 97, 97);font-size:12px;margin-top:1.5px;`
            + SuggestionRendererUtils.getDirectionalCss()
            + `'> ${suggestionType}</span>`;
    }

    private static toggleIconHtml =
        `<span class="icon" aria-hidden="true" style="float:right;font-size:20px;margin-top:-4px;">
            <svg class="uitk-icon-svg">
                <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#uitk-icon-toggle90"></use>
            </svg>
        </span>`;

    private static hasTla(suggestion: ISuggestionResult) {
        if (suggestion && suggestion.regionNames && suggestion.regionNames.displayName) {
            return SuggestionRendererUtils.removeBoldTags(suggestion.regionNames.displayName).match("\\(([A-Z]{3})\\)");
        }
        return false;
    }

    private static removeBoldTags(str: string): string {
        return str.replace(/<B>/g, "").replace(/<\/B>/g, "");
    }

    private static getDirectionalCss(): string {
        return TypeaheadUtils.isRtl() ? "float:left;margin-right:auto;" : "float:right;margin-left:auto;";
    }
}
